import { chunk, find } from 'lodash';
/**
   * @ngdoc service
   * @name adjust.service:Scroll
   *
   * @description
   * Infinite scroll service
   *
   */
function Scroll($filter, $timeout) {
  'ngInject';
  /**
   * @ngdoc property
   * @name _defaults
   * @propertyOf adjust.service:Scroll
   *
   * @description
   * Default scroll parameters
   *
   * @type {Object}
   * @private
   */
  var _defaults = {
    total: 0,
    size: 20,
    pages: [],
    current: 1,
    order: {
      by: 'name',
      reverse: false,
      simple: true
    }
  };

  /**
   * @ngdoc property
   * @name _size
   * @propertyOf adjust.service:Scroll
   *
   * @description
   * Size of the page's chunk
   *
   * @type {Object}
   */
  var _size = {};

  /**
   * @ngdoc property
   * @name _total
   * @propertyOf adjust.service:Scroll
   *
   * @description
   * Total number of pages
   *
   * @type {Object}
   */
  var _total = {};

  /**
   * @ngdoc property
   * @name _order
   * @propertyOf adjust.service:Scroll
   *
   * @description
   * Order array details, predicate and direction
   *
   * @type {Object}
   */
  var _order = {};

  /**
   * @ngdoc property
   * @name _pages
   * @propertyOf adjust.service:Scroll
   *
   * @description
   * Pages array
   *
   * @type {Object}
   */
  var _pages = {};

  /**
   * @ngdoc property
   * @name _current
   * @propertyOf adjust.service:Scroll
   *
   * @description
   * Current page
   *
   * @type {Object}
   */
  var _current = {};

  var _loading = {};
  var _remote = {};
  var _remoteLoaded = {};

  return {
    setList: setList,
    getCurrentPage: getCurrentPage,
    setNextPage: setNextPage,
    updateStream: updateStream,
    hasNext: hasNext,
    isLoaded: isLoaded,
    setUpdating: setUpdating,
    isLoading: isLoading,
    setRemote: setRemote,
    setLoadedRemote: setLoadedRemote,
    orderBy: orderBy,
    destroy: destroy
  };

  /**
   * @ngdoc method
   * @name setList
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Break list into pages
   *
   * @param {string} instance scrolling instance
   * @param {Array} raw original array which needs to be broken into pages
   * @param {string=} order order details
   */
  function setList(instance, raw, order) {

    _reset(instance);

    _order[instance] = order || (_order[instance] || _defaults.order);

    var list = orderBy(instance, raw);

    _pages[instance] = chunk(list, _size[instance]);
    _total[instance] = _pages[instance].length;

  }

  /**
   * @ngdoc method
   * @name orderBy
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Order by functionality
   *
   * @param {string} instance scrolling instance
   * @param {Array} raw raw list to order
   * @returns {Array} ordered list
   */
  function orderBy(instance, raw) {

    // TODO refactor this, PLEASE!
    var fun;
    var value;

    if (_order[instance].simple) {
      fun = _order[instance].by;
    } else if (_order[instance].proxyFunc) {
      fun = function(item) {
        return _order[instance].proxyFunc(item, _order[instance].data);
      };
    } else {
      fun = _order[instance].fun || function(item) {

        if (item.aggregate) {
          return item;
        }

        if (_order[instance].periods) {

          if (_order[instance].by === 'for-graph') {

            value = find(_order[instance].values, {token: item.token});

            return value && value.periods ? value.periods[0].kpi_values[0] : 0;
          } else {
            var tmp = _order[instance].by.split('__');
            var period = find(item.periods, {period: tmp[0]});

            return period ? period.kpi_values[tmp[1]] : 0;
          }
        }

        return item.kpi_values[_order[instance].kpiNames.indexOf(_order[instance].by)];
      };
    }

    return $filter('orderBy')(raw, fun, _order[instance].reverse);

  }

  /**
   * @ngdoc method
   * @name getCurrentPage
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Get current page array
   *
   * @param {string} instance scrolling instance
   * @returns {Array} current page array
   */
  function getCurrentPage(instance) {

    return _pages[instance][_current[instance] - 1];

  }

  /**
   * @ngdoc method
   * @name setNextPage
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Set current page and return it
   *
   * @param {string} instance scrolling instance
   */
  function setNextPage(instance) {

    if (!hasNext(instance)) { return; }

    _current[instance] += 1;

  }

  /**
   * @ngdoc method
   * @name updateStream
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Update scroll stream with new page
   *
   * @param {string} instance scrolling instance
   * @param {Array} list current list
   * @returns {Array} updated list
     */
  function updateStream(instance, list = []) {

    var newList;

    if (hasNext(instance)) {

      setNextPage(instance);

      var newPage = getCurrentPage(instance);
      newList = list.concat(newPage);

    } else {

      newList = list.concat([]);

    }

    return $timeout(function() {
      return newList;
    });

  }

  /**
   * @ngdoc method
   * @name hasNext
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Check if there is next page
   *
   * @param {string} instance scrolling instance
   * @returns {boolean} has next page
   */
  function hasNext(instance) {

    if (_remote[instance]) {
      return !_remoteLoaded[instance];
    }

    return !((_current[instance] + 1) > _total[instance]);
  }

  /**
   * @ngdoc method
   * @name isLoaded
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Is entire list loaded
   *
   * @param {string} instance scrolling instance
   * @returns {boolean} is loaded
   */
  function isLoaded(instance) {

    if (_remote[instance]) {
      return _remoteLoaded[instance];
    }

    return _total[instance] === _current[instance];

  }

  /**
   * @ngdoc method
   * @name setUpdating
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Set updating state
   *
   * @param {string} instance scrolling instance
   * @param {boolean} loading updating state
   */
  function setUpdating(instance, loading) {

    _loading[instance] = loading;

  }

  /**
   * @ngdoc method
   * @name isLoading
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Check the loading state
   *
   * @param {string} instance scrolling instance
   * @returns {boolean} loading state
   */
  function isLoading(instance) {

    return _loading[instance];

  }

  /**
   * @ngdoc method
   * @name setRemote
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Set remote state, tells if list has been loaded from the remote resource
   *
   * @param {string} instance scrolling instance
   * @param {boolean} value if list is remote or not
   */
  function setRemote(instance, value) {

    _loading[instance] = false;
    _remoteLoaded[instance] = false;
    _remote[instance] = value;

  }

  /**
   * @ngdoc method
   * @name setLoadedRemote
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Marks the list as loaded or not
   *
   * @param {string} instance scrolling instance
   * @param {boolean} value if list is loaded or not
   */
  function setLoadedRemote(instance, value) {

    _remoteLoaded[instance] = value;

  }

  /**
   * @ngdoc method
   * @name _reset
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Return parameters to default state
   *
   * @param {string} instance scrolling instance
   * @private
   */
  function _reset(instance) {

    _total[instance] = _defaults.total;
    _pages[instance] = angular.copy(_defaults.pages);
    _current[instance] = _defaults.current;
    _size[instance] = _defaults.size;
    _loading[instance] = _defaults.loading;

    _remote[instance] = false;
    _remoteLoaded[instance] = false;

  }

  /**
   * @ngdoc method
   * @name destroy
   * @methodOf adjust.service:Scroll
   *
   * @description
   * Do the reset and clear event callbacks
   *
   * @param {string} instance scrolling instance
   */
  function destroy(instance) {

    _reset(instance);
    _order[instance] = null;

  }
}

export { Scroll };
